import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
} from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import {
  getActiveOrganisation,
  Organisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  AlertSeverity,
  InvitationAlertState,
  Role,
  UserMembership,
} from "data/models";
import { submitOrganisationInvitations } from "data/mutations-common";
import {
  fetchOrganisationMemberships,
  fetchSubscription,
} from "data/queries-common";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatTimestamp } from "utils";
import { resolveIdentityId } from "utils/identity";
import { InvitationItem, MemberInvitationModal } from "./invite-members";
import { InvitationList } from "./organisation-invitation-list";

interface AlertState {
  message: string;
  severity: AlertSeverity;
}

const severityToVariant = (severity: AlertSeverity) => {
  if (severity === "error") return "destructive" as const;
  return severity;
};

export const OrganisationMembers = () => {
  const [identityState] = useIdentity();
  const currentOrganisation: Organisation | false =
    getActiveOrganisation(identityState);

  const queryClient = useQueryClient();
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [alertStateList, setAlertStateList] = useState<AlertState[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [showGiftedCollaborationAlert, setShowGiftedCollaborationAlert] =
    useState(true);

  useEffect(() => {
    if (showAlert) {
      const timeId = setTimeout(() => {
        setShowAlert(false);
      }, 5000);

      return () => {
        clearTimeout(timeId);
      };
    }
  }, [showAlert]);

  const currentOrg = currentOrganisation as Organisation;
  const membersQuery = useQuery({
    queryKey: [QueryKeys.organisationInvitations, currentOrg?.organisationId],
    queryFn: () => fetchOrganisationMemberships(currentOrg!.organisationId),
    enabled: !!currentOrganisation,
  });

  const invitationMutation = useMutation({
    mutationFn: (invitations: InvitationItem[]) =>
      submitOrganisationInvitations(invitations, currentOrg!.organisationId),
    onSuccess: (alerts: InvitationAlertState[]) => {
      setAlertStateList(alerts);
      setShowAlert(true);
      setInvitationModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationInvitations],
      });
    },
  });

  const identityId = resolveIdentityId(identityState);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  const activeSub = subscriptionQuery.data?.activeSubscription;
  const hasScheduledUpgradeOrDowngrade = activeSub?.nextSub != null;
  const hasScheduledCancellation = activeSub?.cancelAtPeriodEnd === true;

  const futurePlanPermissionKey = subscriptionQuery.data?.activeSubscription
    ?.nextSub?.plan.product_key
    ? [subscriptionQuery.data.activeSubscription?.nextSub.plan.product_key]
    : [];

  const {
    isPermitted: futurePlanIsPermitted,
    isPermissionsSuccess: isFuturePlanPermissionsSuccess,
  } = usePermissions({
    permissionSets: futurePlanPermissionKey,
  });

  const { getAdminOverride } = usePermissions();

  const hasGiftedCollaboration =
    getAdminOverride(PermissionsKeys.membersCreate) === "allow";

  const onAdminInitiatedTrial =
    subscriptionQuery.data?.activeSubscription?.trialType == "admin";

  const isInvitationsListLoaded = membersQuery.isSuccess;

  const isSubscriptionReady = subscriptionQuery.isSuccess;

  const isFuturePlanReady = isFuturePlanPermissionsSuccess;

  const hasBlockingCollaboration =
    hasScheduledCancellation ||
    (hasScheduledUpgradeOrDowngrade &&
      !futurePlanIsPermitted([PermissionsKeys.membersCreate]));

  const hasOwner = getOwner(membersQuery.data ?? []);

  const showCollaborationEndingAlert =
    isInvitationsListLoaded &&
    isSubscriptionReady &&
    !onAdminInitiatedTrial &&
    isFuturePlanReady &&
    hasBlockingCollaboration &&
    hasOwner &&
    !hasGiftedCollaboration;

  return (
    <div className="space-y-2">
      <MemberInvitationModal
        isOpen={invitationModalOpen}
        isLoading={invitationMutation.isPending}
        isError={invitationMutation.isError}
        handleClose={() => setInvitationModalOpen(false)}
        handleSubmit={(itemList: InvitationItem[]) =>
          invitationMutation.mutate(itemList)
        }
        orgName={(currentOrganisation as Organisation).name}
      />
      <div className="flex justify-end">
        <PermissionTooltipWrapper
          permissionKeys={[PermissionsKeys.membersCreate]}
        >
          <Button
            onClick={() => setInvitationModalOpen(true)}
            disabled={
              !currentOrganisation ||
              invitationMutation.isPending ||
              (membersQuery.isEnabled && membersQuery.isPending)
            }
          >
            {membersQuery.isEnabled && membersQuery.isPending ? (
              <Spinner />
            ) : (
              <Mail className="size-4" />
            )}
            Invite Members
          </Button>
        </PermissionTooltipWrapper>
      </div>

      {hasGiftedCollaboration && showGiftedCollaborationAlert && (
        <Alert variant="success" className="mb-4">
          <AlertDescription>
            This organisation has been gifted Collaboration by Geoscape Support.
            You can now invite other members to join your organisation.
          </AlertDescription>
          <AlertAction>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowGiftedCollaborationAlert(false)}
            >
              ✕
            </Button>
          </AlertAction>
        </Alert>
      )}

      {!hasGiftedCollaboration && (
        <PermissionsWrapper
          keys={[PermissionsKeys.membersCreate]}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <Alert variant="info">
                <AlertTitle>Ready to Grow Your Team?</AlertTitle>
                <AlertDescription>
                  Unlock the ability to invite members to your organisation by
                  upgrading to our Team or Pro plan.
                </AlertDescription>
                <AlertAction>
                  <UpgradeSubscriptionButton />
                </AlertAction>
              </Alert>
            ),
          }}
        >
          <PermissionsWrapper
            keys={[PermissionsKeys.membersCreate]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert variant="info">
                  <AlertTitle>Ready to Grow Your Team?</AlertTitle>
                  <AlertDescription>
                    Unlock the ability to invite members to your organisation by
                    upgrading to our Team or Pro plan.
                  </AlertDescription>
                  <AlertAction>
                    <UpgradeSubscriptionButton />
                  </AlertAction>
                </Alert>
              ),
            }}
          ></PermissionsWrapper>
          <PermissionsWrapper
            keys={[PermissionsKeys.rolesUpdate]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert variant="info">
                  <AlertTitle>Unlock Roles & Permissions with Pro</AlertTitle>
                  <AlertDescription>
                    Roles and Permissions allow you to define specific access
                    levels for your team members. This feature is included in
                    our Pro plan.
                  </AlertDescription>
                  <AlertAction>
                    <UpgradeSubscriptionButton />
                  </AlertAction>
                </Alert>
              ),
            }}
          ></PermissionsWrapper>
        </PermissionsWrapper>
      )}
      {showCollaborationEndingAlert && (
        <Alert variant="warning" className="mt-4">
          <AlertTitle>
            Upcoming Change: Collaboration Ending{" "}
            {formatTimestamp(
              (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                1000,
            )}
          </AlertTitle>
          <AlertDescription>
            Please note: Your plan is scheduled to downgrade on{" "}
            {formatTimestamp(
              (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                1000,
            )}
            . This will result in the loss of your organisation's collaboration
            features, and only the owner will be able to use Geoscape Hub.
          </AlertDescription>
          <AlertAction>
            <UpgradeSubscriptionButton />
          </AlertAction>
        </Alert>
      )}

      {showAlert && renderAlerts(alertStateList)}
      <PermissionsWrapper
        keys={[PermissionsKeys.membersRead]}
        messages={{
          [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
        }}
      >
        <InvitationList
          memberships={membersQuery.data ?? []}
          isLoading={membersQuery.isPending}
        />
      </PermissionsWrapper>
    </div>
  );
};

const getOwner = (invitationList: UserMembership[]): string | null => {
  const findByRole = (role: Role): string | undefined =>
    invitationList.find((invitation) => invitation.role == role)?.userEmail;

  return findByRole(Role.OWNER) || null;
};

const renderAlerts = (alertStateList: AlertState[]) => {
  return (
    <div className="flex w-full flex-col gap-2">
      {alertStateList
        .filter((m) => m.message)
        .map((alert: AlertState, index) => (
          <Alert key={index} variant={severityToVariant(alert.severity)}>
            <AlertDescription>{alert.message}</AlertDescription>
          </Alert>
        ))}
    </div>
  );
};
